import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SparklesIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/outline";
import {
  addServiceThunk,
  updateServiceThunk,
  deleteServiceThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const SERVICE_TYPES = [
  { _id: "CUSTOM_DESIGN", name: "Custom Design" },
  { _id: "REPAIR", name: "Repair" },
  { _id: "CLEANING", name: "Cleaning" },
  { _id: "POLISHING", name: "Polishing" },
  { _id: "RESIZING", name: "Resizing" },
  { _id: "HALLMARKING", name: "Hallmarking" },
  { _id: "CERTIFICATION", name: "Certification" },
  { _id: "APPRAISAL", name: "Appraisal" },
  { _id: "EXCHANGE", name: "Exchange" },
  { _id: "BUYBACK", name: "Buyback" },
  { _id: "CONSULTATION", name: "Consultation" },
  { _id: "ENGRAVING", name: "Engraving" },
  { _id: "STONE_SETTING", name: "Stone Setting" },
  { _id: "OTHER", name: "Other" },
];

const INITIAL_FORM = {
  type: "",
  name: "",
  description: "",
  icon: "",
  estimatedTime: "",
  priceRange: { min: "", max: "" },
  isComplimentary: false,
  requiresAppointment: false,
  isActive: true,
};

const ServicesSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { services, loadingSection } = useSelector((state) => state.branchDisplay);

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAdd = async () => {
    await dispatch(addServiceThunk({ branchCode, service: formData }));
    setIsAdding(false);
    setFormData(INITIAL_FORM);
  };

  const handleEdit = (service) => {
    setEditingId(service._id);
    setFormData({
      type: service.type || "",
      name: service.name || "",
      description: service.description || "",
      icon: service.icon || "",
      estimatedTime: service.estimatedTime || "",
      priceRange: {
        min: service.priceRange?.min || "",
        max: service.priceRange?.max || "",
      },
      isComplimentary: service.isComplimentary || false,
      requiresAppointment: service.requiresAppointment || false,
      isActive: service.isActive ?? true,
    });
  };

  const handleUpdate = async () => {
    await dispatch(
      updateServiceThunk({
        branchCode,
        serviceId: editingId,
        serviceData: formData,
      })
    );
    setEditingId(null);
    setFormData(INITIAL_FORM);
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      await dispatch(deleteServiceThunk({ branchCode, serviceId }));
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(INITIAL_FORM);
  };

  const renderForm = () => (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Dropdown
            label="Service Type"
            options={SERVICE_TYPES}
            selected={SERVICE_TYPES.find((t) => t._id === formData.type)}
            onChange={(selected) => handleInputChange("type", selected?._id || "")}
            placeholder="Select type"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Service Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., Custom Ring Design"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
          rows="2"
          placeholder="Describe the service..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Estimated Time</label>
          <input
            type="text"
            value={formData.estimatedTime}
            onChange={(e) => handleInputChange("estimatedTime", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., 2-3 days"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Min Price (₹)</label>
          <input
            type="number"
            value={formData.priceRange.min}
            onChange={(e) => handleInputChange("priceRange.min", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Max Price (₹)</label>
          <input
            type="number"
            value={formData.priceRange.max}
            onChange={(e) => handleInputChange("priceRange.max", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="0"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isComplimentary}
            onChange={(e) => handleInputChange("isComplimentary", e.target.checked)}
            className="h-4 w-4 rounded"
          />
          <span className="text-sm">Complimentary Service</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requiresAppointment}
            onChange={(e) => handleInputChange("requiresAppointment", e.target.checked)}
            className="h-4 w-4 rounded"
          />
          <span className="text-sm">Requires Appointment</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleInputChange("isActive", e.target.checked)}
            className="h-4 w-4 rounded"
          />
          <span className="text-sm">Active</span>
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={editingId ? handleUpdate : handleAdd}
          disabled={loadingSection === "services" || !formData.name || !formData.type}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          {loadingSection === "services" ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <CheckCircleIcon className="h-4 w-4" />
          )}
          {editingId ? "Update Service" : "Add Service"}
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Button */}
      {isRootAdmin && !isAdding && !editingId && (
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Service
        </button>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingId) && renderForm()}

      {/* Services List */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <SparklesIcon className="h-4 w-4 mr-2" />
          Store Services ({services.length})
        </h4>

        {services.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <SparklesIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No services added yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service) => (
              <div
                key={service._id}
                className={`bg-white border rounded-lg p-4 ${
                  !service.isActive ? "opacity-60" : ""
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-medium">{service.name}</h5>
                      {service.isComplimentary && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          Free
                        </span>
                      )}
                      {!service.isActive && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {SERVICE_TYPES.find((t) => t._id === service.type)?.name || service.type}
                    </p>
                    {service.description && (
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                      {service.estimatedTime && (
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          {service.estimatedTime}
                        </span>
                      )}
                      {(service.priceRange?.min || service.priceRange?.max) && (
                        <span className="flex items-center gap-1">
                          <CurrencyRupeeIcon className="h-4 w-4" />
                          {service.priceRange.min && `₹${service.priceRange.min}`}
                          {service.priceRange.min && service.priceRange.max && " - "}
                          {service.priceRange.max && `₹${service.priceRange.max}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {isRootAdmin && !editingId && (
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleEdit(service)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(service._id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesSection;
